import { useMutation } from '@apollo/client';
import type { InsertDashboardMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertDashboardDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { InsertDashboardTRPCInput } from './useInsertDashboardTRPC';
import { useInsertDashboardTRPC } from './useInsertDashboardTRPC';

export const useInsertDashboard = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertDashboardGraphQL, graphqlState] = useMutation(
    InsertDashboardDocument,
    {
      refetchQueries: [namedOperations.Query.getDashboards],
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertDashboardTRPC();

  const insertDashboard = async (
    variables: InsertDashboardTRPCInput
  ): Promise<InsertDashboardMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertDashboard(variables);
    }

    const result = await insertDashboardGraphQL({
      variables: {
        Name: variables.Name,
        Description: variables.Description,
        Sharing: variables.Sharing,
        Content: variables.Content,
        ContributorUserIds: variables.ContributorUserIds,
        ContributorGroupIds: variables.ContributorGroupIds,
      },
    });
    if (!result.data) {
      throw new Error('Failed to insert dashboard');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      insertDashboard,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertDashboard,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
